import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import _ from "lodash";
import { Link, useHistory, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import axios from 'axios'
import { addSuccessAlert, toggleGlobalLoader } from "../../../store/calls/alertLoadingDispatch";
import {
    mountApplication,
    prepareApplicationFormFlow,
    pullApplication,
    updateOrCreateApplication,
    pullApplicationBids
} from "../../../store/calls/applicationData";
import { getJsonFromUrl } from "../../../utils/parseUrl";
import api from "../../../store/api";
import FormFlowWrapper from "../../theme/wrappers/FormFlowWrapper";
import Indicative from "./Indicative";
import formatters from "../../../utils/forms/formatters";
import KeyValPair from "../../theme/data/KeyValuePair";
import * as actions from "../../../store/actions"
import PrintableHeader from '../../theme/printableHeader/printableHeader';
import { getImprovementById, getProjectAmountFromProjectImprovements } from "../../../utils/projectDataUtils";
import { isUserAdmin } from "../../../utils/session/perms";
import AlertCard from "../../theme/cards/AlertCard";
import LoanEstimateCard from "../../subcomponents/loanEstimates/loanEstimateCard";
import ProjectCard from "../../subcomponents/loanEstimates/ProjectCard";
import SignatureModal from "../../subcomponents/application/SignatureModal";
import ApplicationPermissionsModal from "../../subcomponents/application/ApplicationUserPermissionsModal";
import LoanEstimateCardPrintable from "../../subcomponents/loanEstimates/loanEstimateCardPrintable";
import PaymentModal from "../../subcomponents/application/PaymentModal";
import User from '../../../store/reducers/user';
import BaseModal from '../../theme/modals/BaseModal';
import ApplicationFileUpload from './ApplicationFileUpload';
import TelephoneNumber from '../../subcomponents/TelephoneNumber';
import ContactEmail from '../../subcomponents/ContactEmail';

import ApplicationBidModal from '../../subcomponents/application/ApplicationBidModal';
import BidDisplay from '../../subcomponents/application/BidDisplay';
import { getStatusOptions, getUserStatusOptions, UserStatusDisplay, AdminStatusDisplay } from '../../subcomponents/application/StatusDisplay';




const getListOfFilesForDisplay = (applicationData, onUploadRequest) => {
    const files = (applicationData.files || [])
        .sort((a, b) => DateTime.fromISO(a.created) > DateTime.fromISO(b.created) ? -1 : 1)

    const requirements = (applicationData.template?.elements || [])
        .filter(d => (d.type === 'file' && d.required))

    // need to get only latest file for each dataKey
    const datakeys = [...new Set(files.map(d => d.datakey))]
    const displayableFiles = datakeys.map(d => files.find(f => f.datakey === d))

    // now we add required files that haven't been uploaded.
    const displayable_requirements = requirements
        .filter(d => (!files.find(f => d.id === f.datakey)))

    const uploadedFiles = displayableFiles.map(f => ({
        key: requirements.find(r => r.id === f.datakey)?.name || f.description,
        val: <span>
            Uploaded on {formatters.date().formatter(f.created)}
            {f.link && <a className="ml-3" href={f.link} target="_blank" rel="noreferrer">View this file</a>}
        </span>
    }))

    const requirementWarnings = displayable_requirements.map(f => ({
        key: f.name,
        val: <span className="has-text-danger">
            <i className="fas fa-exclamation-triangle mr-3" />
            <span>This document is required. {onUploadRequest && <a href='#' onClick={() => onUploadRequest(f)}>Upload Now</a>}</span>
        </span>
    }))

    return [...uploadedFiles, ...requirementWarnings]
}


const getListOfResponsesForDisplay = (applicationData, excludedKeys = []) => {
    let response_data = (applicationData.data?.responses || [])


    const responses = response_data
        .filter(d => d.response)
        .filter(d => !excludedKeys.includes(d.id))
        .map(d => ({ key: d.name, val: d.response }))

    const missing_requirements = (applicationData.template?.elements || [])
        .filter(d => (
            d.type === 'question' &&
            // d.required &&
            !response_data.filter(d => d.response).find(f => d.id === f.id)
        ))
        .map(d => ({
            key: d.name,
            val: <span className="has-text-danger">
                <i className="fas fa-exclamation-triangle mr-3" />
                <span>A response is required.</span>
            </span>
        }))


    return [...responses, ...missing_requirements]
}


const getListOfLoansForDisplay = (applicationData) => {
    let loans_data = applicationData.data?.loans || []
    /*
    *   loan object keys
    {
        "amount": "300000",
        "balance": null,
        "loan_type": "cmbs",
        "holder_name": "Bank 1",
        "contact_name": null,
        "contact_email": null,
        "contact_phone": null,
        "loan_seniority": "1",
        "original_loan_term": null,
        "annual_payment_amount": null,
        "last_payment_schedule_date": null
    }
}
    * */
    let loans_display = loans_data.map(d => ({
        key: `${d.loan_type}`,
        val: <>
            <p className="m-0">{d.holder_name}</p>
            <p className="m-0">Amount: {formatters.USDCurrency.formatter(d.amount)}</p>
            <p className="m-0">Balance: {formatters.USDCurrency.formatter(d.balance)}</p>
            {!!d.original_loan_term && <p className="m-0">Loan Term: {d.original_loan_term}</p>}
            <p className="m-0">Contact: {d.contact_name}, {d.contact_email}, {d.contact_phone}</p>

        </>
    }))

    if (!loans_display.length) {
        loans_display = [{
            key: '--',
            value: 'No Loans Added'
        }]
    }

    return loans_display
}

const getMissingApplicationRequirements = applicationData => {

    const requirements = (applicationData.template?.elements || []).filter(d => d.required === true)
    const missing_requirements = requirements.filter(d => {
        const match = (applicationData.data?.responses || []).find(r => r.id === d.id) ||
            (applicationData.files || []).find(f => f.datakey === d.id)

        if (match) {
            // positive response of link value, depending on whether in response array or files array
            return !(match.response || match.link)
        }
        return true

    });

    return missing_requirements;
}


function ApplicationEditButton({ targetStep = 1 }) {

    const application_id = useSelector(state => state.applicationData?.app?.id)
    const dispatch = useDispatch();
    const history = useHistory();

    const onButtonClick = (ev) => {
        prepareApplicationFormFlow();
        dispatch(actions.formFlow.setFormFlowCurrStep(targetStep))
        history.push(`/apply/${application_id}`)
    }

    return (
        <button
            className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
            onClick={onButtonClick}
        >
            Edit
        </button>
    )
}

function ApplicationReviewSection({ title, editStep, data, customTopButtons = <></> }) {

    let user_permissions = useSelector(state => state.applicationData?.app?.requester_permissions || [])

    let user_can_edit = user_permissions.includes('edit');
    let user_can_bid = user_permissions.includes('bid');

    return (
        <div className="application-review-section">

            <div className="card mt-2 mb-6">

                <div className="card-body">
                    <div className="card-header has-background-primary">
                        <div className="card-header-title has-text-white">
                            <div className="is-flex is-align-items-center is-justify-content-space-between is-fullwidth">
                                <h3 className="is-size-5 has-text-white mr-6">{title}</h3>
                                <div>
                                    {user_can_edit && <ApplicationEditButton targetStep={editStep} />}
                                    {customTopButtons}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-content">
                        {data.map(d => (
                            <KeyValPair key={d.key} _key={d.key} val={d.val} striped />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}


function ApplicationAlertList({ onSignRequest, onPayRequest }) {
    const applicationData = useSelector(state => state.applicationData?.app);
    const product_type = useSelector(state => state.applicationData?.indicative?.data?.product?.type)
    const [missingReqs, setMissingReqs] = useState([])
    const user_permissions = useSelector(state => state.applicationData?.app?.requester_permissions || [])
    const is_biddable = useSelector(state => state.applicationData?.app?.is_biddable || state.applicationData?.indicative?.data?.product?.type == 'PPA')
    const has_selected_a_bid = useSelector(state => !!state.applicationData?.indicative?.bid_by)



    const user_permissions_map = {
        'edit': 'Edit Application',
        'bid': "Make a bid on this project",
        'upload_files': 'Upload files, and view only the files that you have uploaded',
        'view': 'View all application materials',
    }


    useEffect(() => {
        setMissingReqs(getMissingApplicationRequirements(applicationData))
    }, [applicationData])

    const is_signed = applicationData.signature?.signatureValue;
    const is_paid = applicationData.payment?.id

    return (
        <>
            {missingReqs.length > 2 &&
                <AlertCard
                    className="mb-4"
                    type="warning"
                >
                    <div>
                        <p className="has-text-warning-dark has-text-weight-semibold">
                            {/* There are {missingReqs.length} incomplete requirements. */}
                            Some documents or answers may be missing. Please review the application.
                        </p>
                    </div>
                </AlertCard>
            }
            {!user_permissions.includes('edit') &&
                <AlertCard
                    className="mb-4"
                    type="warning"
                >
                    <div className='content has-text-warning-dark '>
                        <p className='has-text-weight-semibold'>
                            You have limited permissions on this application. You may:
                        </p>
                        <ul>
                            {user_permissions.map(p => <li>{user_permissions_map[p] || p}</li>)}
                        </ul>
                    </div>
                </AlertCard>

            }
            {product_type == 'PPA' &&
                <AlertCard
                    className="mb-4"
                    type="success"
                    msg={<span className="has-text-weight-semibold has-text-success-dark">
                        You are submitting a project to receive PPA bids from investors via your CleanFi account. There is no application fee until you have accepted a bid. Should you need clarification or assistance, please
                        contact CleanFi at <TelephoneNumber /> or write to <ContactEmail />. Please include the unique link of this page in your correspondence.
                    </span>}
                />}

            {(is_signed && is_paid) ?
                <AlertCard
                    className="mb-4"
                    type="success"
                    msg={<span className="has-text-weight-semibold has-text-success-dark">
                        This application has been successfully submitted. It was signed by {applicationData.signature?.signatureValue}
                        {applicationData.signature?.signedTime ?
                            ` on ${formatters.date().formatter(applicationData.signature.signedTime)}` :
                            null}

                    </span>}
                /> :
                <>

                    {!is_signed && <AlertCard
                        className="mb-4"
                        type="info"
                        msg={
                            <div className="columns">
                                <div className="column is-three-quarters-desktop has-text-weight-semibold">
                                    This application must be signed when it is complete. Your signature lets us know that you are ready for it to be processed by our team, 
                                    and we cannot send out a file for bid or begin pre-underwriting until it is signed.
                                    
                                </div>
                                <div className="column">
                                    <button className="button is-medium is-link" onClick={onSignRequest}>Sign Application</button>
                                </div>
                            </div>
                        }
                    />}
                    {!is_paid && (!is_biddable || has_selected_a_bid) &&
                        <AlertCard
                            className="mb-4"
                            type="info"
                            msg={
                                <div className="columns">
                                    <div className="column is-three-quarters-desktop">
                                        This application has not yet been submitted because it requires an application fee of&nbsp;
                                        <span className='has-text-weight-semibold'>{(product_type || '').includes('PACE') ? "$250" : "$95"}.</span>
                                    </div>
                                    <div className="column">
                                        <button className="button is-medium is-link" onClick={onPayRequest}>Pay Fee</button>
                                    </div>
                                </div>
                            }
                        />}
                    <AlertCard
                        className="mb-4"
                        type="info"
                        msg={<div>
                            <p className="">We're here to help! Email <ContactEmail className='has-text-grey-darker has-text-weight-semibold' /> or
                                call us at <TelephoneNumber className='has-text-grey-darker has-text-weight-semibold' /> with any questions.</p>
                            <p className=''>
                                You can come back to this application at any time to edit through 'Applications' tab on the My Projects page.
                            </p>
                        </div>}
                    />
                </>

            }
        </>
    )


}


const onApplicationSign = async (sigData, applicationData, userData) => {

    let ip_res;

    try {
        ip_res = await axios.get('https://geolocation-db.com/json/')
    } catch (e) {
        ip_res = { data: 'Failed to update IP Data' }
    }

    let data = {
        id: applicationData.id,
        signature: {
            ...sigData,
            ip: ip_res.data
        }
    };



    return updateOrCreateApplication(data)
}


const onApplicationFeePay = async (paymentData, applicationData) => { }



function ApplicationView(props) {

    const applicationData = useSelector(state => state.applicationData?.app);
    const projectData = useSelector(state => state.applicationData.projectLead);
    const product_type = useSelector(state => state.applicationData?.indicative?.data?.product?.type)
    const selectedLoanEstimate = useSelector(state => state.applicationData.indicative);

    const user_permissions = useSelector(state => state.applicationData?.app?.requester_permissions || [])
    const is_biddesk = useSelector(state => state.applicationData?.app?.is_bid || state.applicationData?.app?.is_biddable)
    const app_bids = useSelector(state => state.applicationData?.bids || [])


    const [signatureModalOpen, setSignatureModalOpen] = useState(false)
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [contributorModalOpen, setContributorModalOpen] = useState(false);
    const [addFileModalOpen, setAddFileModalOpen] = useState(false);
    const [bidModalOpen, setBidModalOpen] = useState(false);



    const [mode, setMode] = useState('app_data')

    // SET PAYMENT AMOUNT BASED ON TYPE
    const application_payment_amount = product_type == 'PACE' ? 395000 : 9500;

    // keys to put in applicant section. Everything else goes in reponses.
    const applicantSectionKeys = ['name', 'phone', 'email', 'company', 'role'];
    const excludedKeys = ['loans']
    const responses = applicationData.data?.responses || [];
    const user_can_bid = user_permissions.includes('bid');
    const user_is_app_admin = user_permissions.includes('edit_requirements');


    let { id: targetApplicationId } = useParams();
    const searchParams = new URLSearchParams(document.location.search)

    let dispatch = useDispatch()

    

    // initial load
    useEffect(() => {
        toggleGlobalLoader(true)
        dispatch(actions.applicationData.resetApplicationData())

        Promise.all([
            pullApplication(targetApplicationId),
            pullApplicationBids(targetApplicationId)
        ])
            .finally(() => toggleGlobalLoader(false))

        if (
            searchParams.get('bid') &&
            !!(user_can_bid || app_bids.length)
        ) {
            setMode('bids')
        }

        
    }, [])

    useEffect(() => {
        getMissingApplicationRequirements(applicationData)
    }, [applicationData])



    // tab generation in the case there are bids
    const generateTabs = () => {
        // if (!bids.length) return <></>

        return (
            <div className="tabs is-boxed mt-5 pb-2 no-print">
                <ul>

                    <li
                        className={mode === 'app_data' ? "is-active has-text-weight-semibold " : ""}
                    >
                        <a onClick={() => setMode('app_data')} className="is-size-5">
                            Application Data
                        </a>
                    </li>
                    {!!(user_can_bid || app_bids.length) &&
                        <li
                            className={mode === 'bids' ? "is-active has-text-weight-semibold " : ""}
                        >
                            <a onClick={() => setMode('bids')} className="is-size-5">
                                Bids
                            </a>
                        </li>
                    }
                </ul>
            </div>
        )
    }



    return (
        <FormFlowWrapper>
            <div className="list-wrapper-one-column">

                <div className="is-flex is-justify-content-flex-start is-flex-direction-column">
                    <h2 className="is-size-3 has-text-primary mb-4"> {product_type == 'PPA' ? "Bid Desk - Project Submission" : "Application"}</h2>
                    <div className='buttons'>
                        {/* (applicationData?.requester_permissions || []).includes('edit_requirements') */}
                        {!!isUserAdmin() &&
                            <>
                                <button className='button ml-2 is-primary' onClick={() => setContributorModalOpen(true)}>
                                    Add contributors
                                </button>
                                <Link to={`/admin/edit-app-req/${applicationData.id}`} target="_blank">
                                    <button className="button">Admin: Edit Application Requirements</button>
                                </Link>
                            </>
                        }
                        {/* {user_can_bid &&
                            // <Link to={`/application/${applicationData.id}/bid`}>
                            <button className="button is-primary no-print no-render-print-view"
                                onClick={() => setBidModalOpen(true)}
                            >
                                Bid
                            </button>
                            // </Link>
                        } */}

                    </div>
                </div>
                <div className="mt-4 mb-6">
                    <ProjectCard project={projectData} />
                </div>

                

                {generateTabs()}


                {/*  START OF APP DATA MODE */}
                {mode === 'app_data' &&
                    <div>

                        {user_is_app_admin ? 
                            <AdminStatusDisplay 
                                {...applicationData}
                                onChange={(status) => updateOrCreateApplication({ id: applicationData.id, status })}
                            />  
                            : <UserStatusDisplay 
                                status={applicationData.status}
                                onChange={(status) => updateOrCreateApplication({ id: applicationData.id, status })}
                            />  
                        }
                        <ApplicationAlertList
                            onSignRequest={() => setSignatureModalOpen(true)}
                            onPayRequest={() => setPaymentModalOpen(true)}
                        />

                        {user_permissions.includes('view') &&
                            <>
                                <ApplicationReviewSection
                                    title="Applicant"
                                    editStep={1}
                                    data={responses
                                        .filter(d => applicantSectionKeys.includes(d.id))
                                        .map(d => ({ key: d.id, val: d.response }))}
                                />
                                <ApplicationReviewSection
                                    title="Responses"
                                    editStep={2}
                                    data={getListOfResponsesForDisplay(applicationData,
                                        [...applicantSectionKeys, ...excludedKeys])}
                                />
                            </>
                        }
                        <ApplicationReviewSection
                            title="Files"
                            editStep={3}
                            data={getListOfFilesForDisplay(applicationData)}
                            customTopButtons={(user_permissions.includes('upload_files') && !user_permissions.includes('edit')) ?
                                <button
                                    className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view"
                                    onClick={() => setAddFileModalOpen(true)}
                                >
                                    Upload Files
                                </button>
                                : null}
                        />
                        {product_type !== 'PPA' && <LoanEstimateCard
                            header={
                                <div className="card-header has-background-primary">
                                    <div className="card-header-title has-text-white">
                                        <div className="is-flex is-align-items-center is-justify-content-space-between is-fullwidth">
                                            <h3 className="is-size-5 has-text-white mr-6">Selected Financing: {selectedLoanEstimate.data?.product?.type}</h3>
                                        </div>
                                    </div>
                                </div>
                            }
                            title={' '}
                            showType={false}
                            estimate={selectedLoanEstimate.data || {}}
                            idx={0}
                            hasPriviledges={false}
                            showApply={false}
                        />}

                        {/* END OF APP_DATA MODE */}
                    </div>}

                {/* START OF BID MODE */}
                {mode === 'bids' && <div>
                    <BidDisplay />
                </div>}
                {/* END OF BID MODE */}
            </div>


            {signatureModalOpen &&
                <SignatureModal
                    title={<h4 className="is-size-4 has-text-primary">Sign Application</h4>}
                    onCancel={() => setSignatureModalOpen(false)}
                    onSign={(sigData) => {
                        onApplicationSign(sigData, applicationData);
                        setSignatureModalOpen(false);

                    }}
                />
            }

            {paymentModalOpen &&
                <PaymentModal
                    txt={<>
                        <h1 className="is-size-1 has-text-centered mb-3">${application_payment_amount / 100}</h1>
                        <p className="has-text-centered mb-3">We will begin processing your application once the fee has been submitted. This fee is non-refundable.</p>
                    </>}
                    onClose={() => setPaymentModalOpen(false)}
                    onComplete={(payment_data) => {
                        addSuccessAlert("Your application was submitted!")
                        setPaymentModalOpen(false);

                        let data = {
                            id: applicationData.id,
                            payment: payment_data.payment
                        };

                        return updateOrCreateApplication(data)

                    }}
                    amount={application_payment_amount}
                />
            }

            {contributorModalOpen &&
                <ApplicationPermissionsModal
                    permissionsAvailable={applicationData.requester_permissions || []}
                    existingPermissions={applicationData.permissions}
                    onClose={() => setContributorModalOpen(false)}
                    applicationData={applicationData}
                />
            }


            {addFileModalOpen &&
                <BaseModal
                    visible
                    onClose={() => setAddFileModalOpen(false)}
                >
                    <div className='p-4'>
                        <ApplicationFileUpload />
                    </div>
                </BaseModal>
            }


            {bidModalOpen &&
                <ApplicationBidModal
                    onCancel={() => setBidModalOpen(false)}
                />
            }
        </FormFlowWrapper>
    )
}


export default ApplicationView;
